import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import AscCrosswordPuzzleCell from "../AscCrosswordPuzzleCell/AscCrosswordPuzzleCell";
import styles from "./AscCrosswordPuzzleBoard.module.scss";
import {
  AscCrosswordPuzzleBoardSize,
  AscCrosswordPuzzleBoardProps,
  AscCrosswordPuzzleWordCell,
  ignoreAttribute,
} from "@ascendnow/shared";
import {
  generateWordCells,
  getWordIndexForFirstCellOfWord,
  groupCharactersByWordIndex,
  mapWordsToCells,
} from "./helpers";

export default function AscCrosswordPuzzleBoard({
  className,
  size = AscCrosswordPuzzleBoardSize.TEN_BY_TEN,
  wordsAcross,
  wordsDown,
  onChange,
  onError,
  onGenerate,
  mode = "solve",
  disabled = false,
}: AscCrosswordPuzzleBoardProps) {
  const boardSizeStyle = {
    "--rows": size,
    "--columns": size,
  } as React.CSSProperties;

  const [cells, setCells] = useState<AscCrosswordPuzzleWordCell[]>([]);
  const cellRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [characters, setCharacters] = useState<string[]>([]);
  const [activeIndex, setActiveIndex] = useState<{
    acrossWordIndex?: number;
    downWordIndex?: number;
  }>();
  const [selectedCellId, setSelectedCellId] = useState<number>();

  function goToNextCellInWord(
    selectedCellId: number,
    activeIndex: Partial<Record<"acrossWordIndex" | "downWordIndex", number>>
  ) {
    const nextCell = cells.find(
      (cell) =>
        cell?.id > selectedCellId &&
        ((!!cell?.acrossWordIndex &&
          cell?.acrossWordIndex === activeIndex?.acrossWordIndex) ||
          (!!cell?.downWordIndex &&
            cell?.downWordIndex === activeIndex?.downWordIndex))
    );
    if (nextCell) {
      setSelectedCellId(nextCell?.id);
      cellRefs?.current?.[nextCell?.id]?.focus?.();
    }
  }

  function handleNavigation(direction: string) {
    let nextCellId;
    switch (direction) {
      case "ArrowUp":
        nextCellId = (selectedCellId || size) - size;
        break;
      case "ArrowDown":
        nextCellId = (selectedCellId || -size) + size;
        break;
      case "ArrowLeft":
        nextCellId = (selectedCellId || size) - 1;
        break;
      case "ArrowRight":
        nextCellId = (selectedCellId || -1) + 1;
        break;
    }

    if (
      nextCellId === undefined ||
      (!cells[nextCellId]?.acrossWordIndex && !cells[nextCellId]?.downWordIndex)
    ) {
      return;
    }

    setSelectedCellId(nextCellId);
    cellRefs?.current?.[nextCellId]?.focus?.();

    if (
      cells[nextCellId]?.acrossWordIndex &&
      !cells[nextCellId]?.downWordIndex
    ) {
      setActiveIndex({
        acrossWordIndex: cells[nextCellId]?.acrossWordIndex,
      });
      return;
    }

    if (
      !cells[nextCellId]?.acrossWordIndex &&
      cells[nextCellId]?.downWordIndex
    ) {
      setActiveIndex({
        downWordIndex: cells[nextCellId]?.downWordIndex,
      });
      return;
    }

    setActiveIndex(
      activeIndex?.acrossWordIndex
        ? {
            downWordIndex: cells[nextCellId]?.downWordIndex,
          }
        : {
            acrossWordIndex: cells[nextCellId]?.acrossWordIndex,
          }
    );
  }

  function handleSelect(id: number) {
    setActiveIndex(
      (activeIndex?.acrossWordIndex && cells[id]?.downWordIndex) ||
        (!activeIndex?.acrossWordIndex && !cells[id]?.acrossWordIndex)
        ? { downWordIndex: cells[id]?.downWordIndex }
        : { acrossWordIndex: cells[id]?.acrossWordIndex }
    );
    setSelectedCellId(id);
  }

  function handleInput(id: number, character: string) {
    const newCharacters = [...characters];
    newCharacters[id] = character;
    setCharacters(newCharacters);
    if (!activeIndex) {
      return;
    }
    goToNextCellInWord(id, activeIndex);
  }

  useEffect(() => {
    const cells = generateWordCells({
      wordsAcross,
      wordsDown,
      size,
      mode,
    });
    setCells(cells);

    if (cells?.length >= size * size) {
      onError?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordsAcross, wordsDown, size, mode]);

  useEffect(() => {
    if (mode !== "solve") {
      return;
    }
    const { wordsAcross, wordsDown } = groupCharactersByWordIndex(
      cells,
      characters
    );

    onChange?.({
      wordsAcross: wordsAcross?.filter?.((w) => w?.word) || [],
      wordsDown: wordsDown?.filter?.((w) => w?.word) || [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [characters, mode]);

  useEffect(() => {
    if (disabled) {
      return;
    }

    if (mode !== "create") {
      return;
    }
    const isError = cells?.some((c) => c?.error);

    if (isError) {
      onError?.(isError);
    }

    onGenerate?.(
      {
        wordsAcross: wordsAcross
          ?.filter((w) => cells[w?.beginningCellNumber]?.acrossWordIndex)
          ?.map(({ word, beginningCellNumber, id }) => ({
            word: word || "",
            id: id || "",
            beginningCellNumber,
            length: word?.length || 0,
            wordIndex: cells[beginningCellNumber]?.acrossWordIndex || 0,
          })),
        wordsDown: wordsDown
          ?.filter((w) => cells[w?.beginningCellNumber]?.downWordIndex)
          ?.map(({ word, beginningCellNumber, id }) => ({
            word: word || "",
            id: id || "",
            beginningCellNumber,
            length: word?.length || 0,
            wordIndex: cells[beginningCellNumber]?.downWordIndex || 0,
          })),
      },
      isError
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cells, wordsAcross, wordsDown, mode, disabled]);

  useEffect(() => {
    if (mode !== "create" && mode !== "completed") {
      return;
    }

    const { characters, errors } = mapWordsToCells({
      wordsAcross,
      wordsDown,
      size,
    });

    setCharacters(characters);
    setCells((prev) =>
      prev.map((cell) => ({
        ...cell,
        error: cell?.error || errors[cell?.id],
      }))
    );
  }, [wordsAcross, wordsDown, mode, size]);

  return (
    <div
      data-testid="asc-crossword-puzzle-board"
      className={classNames(styles.ground, className)}
      style={boardSizeStyle}
      {...ignoreAttribute.contrast}
    >
      {cells?.slice(0, size * size).map((cell) => (
        <AscCrosswordPuzzleCell
          key={cell?.id}
          id={cell?.id}
          ref={(el) => (cellRefs.current[cell?.id] = el)}
          highlighted={
            (!!activeIndex?.acrossWordIndex &&
              activeIndex?.acrossWordIndex === cell?.acrossWordIndex) ||
            (!!activeIndex?.downWordIndex &&
              activeIndex?.downWordIndex === cell?.downWordIndex)
          }
          error={cell?.error}
          readonly={mode === "create" || disabled}
          selected={cell?.id === selectedCellId}
          onSelect={handleSelect}
          onNavigate={handleNavigation}
          onInput={handleInput}
          empty={!cell?.acrossWordIndex && !cell?.downWordIndex}
          wordIndex={getWordIndexForFirstCellOfWord(cell, cells)}
          character={characters[cell?.id]}
        />
      ))}
    </div>
  );
}
